import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Ruler, Mountain, Users, CalendarDays, Crosshair } from "lucide-react";
import api from "../lib/api";
import { BALLPARKS, wallDistanceAt, projectCarry } from "../lib/ballparks";

const HX = 300, HY = 560, SCALE = 1.02;

function pt(deg, dist) {
  const rad = (deg * Math.PI) / 180;
  return [HX + dist * SCALE * Math.sin(rad), HY - dist * SCALE * Math.cos(rad)];
}

function wallPath(dims) {
  const pts = [];
  for (let deg = -45; deg <= 45; deg += 3) {
    pts.push(pt(deg, wallDistanceAt(dims, deg)));
  }
  return pts;
}

export function BallparkModal({ game, onClose, initialBatter }) {
  const park = BALLPARKS[game.home];
  const wx = game.weather;
  const [ev, setEv] = useState(102);
  const [la, setLa] = useState(28);
  const [dir, setDir] = useState(0);
  const [bq, setBq] = useState("");
  const [batter, setBatter] = useState(initialBatter || null);
  const { data: playersData } = useQuery({ queryKey: ["players"], queryFn: () => api.get("/players").then((r) => r.data) });
  const { data: spray, isFetching: sprayLoading } = useQuery({
    queryKey: ["spray", batter?.mlb_id],
    queryFn: () => api.get(`/batter-spray/${batter.mlb_id}`).then((r) => r.data),
    enabled: !!batter,
  });
  const batterMatches = bq.trim().length >= 2
    ? (playersData?.players || []).filter((p) => p.name.toLowerCase().includes(bq.trim().toLowerCase())).slice(0, 6)
    : [];

  if (!park) return null;
  const dims = park.dims;
  const wall = wallPath(dims);
  const fieldPath = `M ${HX} ${HY} L ${wall.map((p) => p.join(" ")).join(" L ")} Z`;
  const carry = projectCarry({
    ev, la,
    temp: wx?.temp, windSpeed: wx?.wind_speed, windDir: wx?.wind_dir,
    elevation: park.elevation,
  });
  const wallDist = Math.round(wallDistanceAt(dims, dir));
  const gone = carry >= wallDist;
  const warning = !gone && carry >= wallDist - 15;
  const land = pt(dir, Math.min(carry, 470));
  const anchors = [
    { deg: -45, d: dims.lf, label: "LF" }, { deg: -22.5, d: dims.lc, label: "LC" },
    { deg: 0, d: dims.cf, label: "CF" }, { deg: 22.5, d: dims.rc, label: "RC" },
    { deg: 45, d: dims.rf, label: "RF" },
  ];
  const diamond = 90 * SCALE;
  const [d1x, d1y] = pt(45, 90), [d3x, d3y] = pt(-45, 90), [d2x, d2y] = pt(0, 90 * Math.SQRT2);
  const sprayDots = (spray?.homers || []).map((h) => {
    const d = h.dist || 380;
    const out = d >= wallDistanceAt(dims, h.angle);
    const [x, y] = pt(h.angle, Math.min(d, 470));
    return { x, y, out, ...h };
  });
  const wouldLeave = sprayDots.filter((s) => s.out).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start lg:items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="ballpark-modal">
      <button onClick={onClose} data-testid="ballpark-modal-close-floating"
        className="fixed top-3 right-3 z-[70] bg-black/80 border border-[#a855f7]/50 rounded-full p-2.5 text-[#a855f7] hover:text-white transition-colors duration-200">
        <X size={18} />
      </button>
      <div className="card-gold border border-[#a855f7]/40 rounded-xl p-4 md:p-6 max-w-4xl w-full my-4 lg:my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight text-[#a855f7]">{park.venue}</h3>
            <div className="text-xs text-neutral-400">{park.city} · {game.away ? `${game.away} @ ${game.home}` : game.home} · {game.time_et}</div>
          </div>
          <button onClick={onClose} data-testid="ballpark-modal-close" className="text-neutral-500 hover:text-white p-1 transition-colors duration-200"><X size={20} /></button>
        </div>

        <div className="grid lg:grid-cols-[1fr_290px] gap-4">
          <div className="bg-[#0d0d0d] border border-white/10 rounded-lg p-2">
            <svg viewBox="10 40 580 560" className="w-full" data-testid="ballpark-svg">
              <path d={fieldPath} fill="#173d1c" stroke="none" />
              <path d={`M ${wall.map((p) => p.join(" ")).join(" L ")}`} fill="none" stroke="#eab308" strokeWidth="2.5" strokeDasharray="7 4" />
              <line x1={HX} y1={HY} x2={pt(-45, dims.lf)[0]} y2={pt(-45, dims.lf)[1]} stroke="#ef4444" strokeWidth="1.5" strokeDasharray="5 4" />
              <line x1={HX} y1={HY} x2={pt(45, dims.rf)[0]} y2={pt(45, dims.rf)[1]} stroke="#ef4444" strokeWidth="1.5" strokeDasharray="5 4" />
              <path d={`M ${HX} ${HY} L ${d1x} ${d1y} L ${d2x} ${d2y} L ${d3x} ${d3y} Z`} fill="#7a5230" opacity="0.9" />
              <circle cx={HX} cy={HY - 60.5 * SCALE} r={9} fill="#8a5f38" />
              {[[HX, HY], [d1x, d1y], [d2x, d2y], [d3x, d3y]].map(([x, y], i) => (
                <rect key={i} x={x - 3} y={y - 3} width="6" height="6" fill="#fff" transform={`rotate(45 ${x} ${y})`} />
              ))}
              {anchors.map((a) => {
                const [x, y] = pt(a.deg, a.d + 26);
                return (
                  <text key={a.label} x={x} y={y} textAnchor="middle" fill="#a855f7" fontSize="13" fontWeight="bold" fontFamily="monospace">
                    {a.label} {a.d}′
                  </text>
                );
              })}
              <line x1={HX} y1={HY} x2={land[0]} y2={land[1]} stroke={gone ? "#22c55e" : warning ? "#eab308" : "#9ca3af"} strokeWidth="2" strokeDasharray="3 3" opacity="0.9" />
              {sprayDots.map((s, i) => (
                <circle key={i} cx={s.x} cy={s.y} r="4.5" fill={s.out ? "#a855f7" : "#6b7280"} stroke="#0a0a0a" strokeWidth="1" opacity="0.92" data-testid={`spray-dot-${i}`}>
                  <title>{s.date} · {s.dist ? `${s.dist} ft` : "distance n/a"} · at {s.park}</title>
                </circle>
              ))}
              <circle cx={land[0]} cy={land[1]} r="7" fill={gone ? "#22c55e" : warning ? "#eab308" : "#9ca3af"} stroke="#0a0a0a" strokeWidth="2" data-testid="trajectory-marker" />
              <text x={HX} y={HY + 24} textAnchor="middle" fill="#888" fontSize="11" fontFamily="monospace">HOME PLATE</text>
            </svg>
          </div>

          <div className="space-y-3">
            <div className="border border-white/10 rounded-lg p-3 grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5 text-neutral-400"><CalendarDays size={12} className="text-[#a855f7]" /> Opened {park.opened}</div>
              <div className="flex items-center gap-1.5 text-neutral-400"><Users size={12} className="text-[#a855f7]" /> {park.capacity.toLocaleString()}</div>
              <div className="flex items-center gap-1.5 text-neutral-400"><Mountain size={12} className="text-[#a855f7]" /> {park.elevation.toLocaleString()} ft elev</div>
              <div className="flex items-center gap-1.5 text-neutral-400"><Ruler size={12} className="text-[#a855f7]" /> {park.surface}</div>
              {wx && <div className="col-span-2 text-neutral-400">{wx.temp}°F · {wx.wind_speed} mph {wx.wind_dir} · {wx.conditions}</div>}
            </div>

            <div className="border border-white/10 rounded-lg p-3" data-testid="spray-lookup">
              <div className="text-[11px] uppercase tracking-widest text-[#a855f7] font-bold mb-2 flex items-center gap-1.5"><Crosshair size={12} /> Batter HR Spray at {park.venue}</div>
              {batter ? (
                <div>
                  <div className="flex items-center gap-2">
                    <img src={batter.headshot} alt="" className="w-8 h-8 rounded-full object-cover bg-white/5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{batter.name}</div>
                      <div className="text-[10px] text-neutral-500">{batter.team} · bats {spray?.stand || batter.bats}</div>
                    </div>
                    <button onClick={() => { setBatter(null); setBq(""); }} data-testid="spray-clear-btn" className="text-xs text-neutral-500 hover:text-red-400 transition-colors duration-200">Change</button>
                  </div>
                  {sprayLoading ? (
                    <div className="text-xs text-neutral-500 animate-pulse mt-2">Pulling Statcast home runs...</div>
                  ) : spray?.summary ? (
                    <div className="mt-2 text-xs text-neutral-400 space-y-1" data-testid="spray-summary">
                      <div><span className="text-white font-data font-bold">{spray.summary.total} HRs</span> in {spray.season} · avg {spray.summary.avg_dist ?? "—"} ft</div>
                      <div>Pull {spray.summary.pull_pct}% · Center {spray.summary.center_pct}% · Oppo {spray.summary.oppo_pct}%</div>
                      <div className="text-[#a855f7] font-semibold">{wouldLeave} of {spray.summary.total} would leave {park.venue}</div>
                      <div className="text-[10px] text-neutral-600">Gold dots clear this wall · gray dots stay in at this park</div>
                    </div>
                  ) : (
                    <div className="text-xs text-neutral-500 mt-2" data-testid="spray-empty">No Statcast homers found for this batter.</div>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <input value={bq} onChange={(e) => setBq(e.target.value)} placeholder="Type a batter's name..." data-testid="spray-batter-search"
                    className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2 text-sm outline-none transition-colors duration-200" />
                  {batterMatches.length > 0 && (
                    <div className="absolute z-30 mt-1 w-full card-surface rounded-lg max-h-48 overflow-y-auto shadow-xl">
                      {batterMatches.map((p) => (
                        <button key={p.mlb_id} onClick={() => { setBatter(p); setBq(""); }} data-testid={`spray-batter-${p.mlb_id}`}
                          className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-neutral-300 hover:bg-[#a855f7]/10 transition-colors duration-200">
                          <img src={p.headshot} alt="" className="w-6 h-6 rounded-full object-cover bg-white/5" loading="lazy" />
                          <span className="truncate">{p.name}</span>
                          <span className="text-neutral-600 text-xs shrink-0">{p.team}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {bq.trim().length >= 2 && batterMatches.length === 0 && <div className="text-xs text-neutral-600 mt-1.5">No batter matches that spelling</div>}
                </div>
              )}
            </div>

            <div className="border border-[#a855f7]/30 rounded-lg p-3" data-testid="trajectory-calculator">
              <div className="text-[11px] uppercase tracking-widest text-[#a855f7] font-bold mb-2">HR Trajectory Calculator</div>
              <label className="text-[10px] uppercase text-neutral-500 font-semibold flex justify-between">Exit Velocity <span className="text-white font-data">{ev} mph</span></label>
              <input type="range" min="85" max="120" value={ev} onChange={(e) => setEv(Number(e.target.value))} data-testid="traj-ev" className="w-full accent-[#a855f7]" />
              <label className="text-[10px] uppercase text-neutral-500 font-semibold flex justify-between mt-2">Launch Angle <span className="text-white font-data">{la}°</span></label>
              <input type="range" min="15" max="45" value={la} onChange={(e) => setLa(Number(e.target.value))} data-testid="traj-la" className="w-full accent-[#a855f7]" />
              <label className="text-[10px] uppercase text-neutral-500 font-semibold flex justify-between mt-2">Direction <span className="text-white font-data">{dir < 0 ? `${Math.abs(dir)}° LF` : dir > 0 ? `${dir}° RF` : "Dead CF"}</span></label>
              <input type="range" min="-45" max="45" value={dir} onChange={(e) => setDir(Number(e.target.value))} data-testid="traj-dir" className="w-full accent-[#a855f7]" />
              <div className={`mt-3 rounded-lg p-3 text-center border ${gone ? "border-[#22c55e]/60 bg-[#22c55e]/10" : warning ? "border-[#eab308]/60 bg-[#eab308]/10" : "border-white/15 bg-black/40"}`} data-testid="traj-verdict">
                <div className={`font-display text-xl font-black uppercase ${gone ? "text-[#22c55e]" : warning ? "text-[#eab308]" : "text-neutral-400"}`}>
                  {gone ? "GONE!" : warning ? "Warning Track" : "Stays in the yard"}
                </div>
                <div className="text-xs text-neutral-400 mt-1 font-data">
                  Projected {carry} ft · wall {wallDist} ft {gone ? `(+${carry - wallDist})` : `(${carry - wallDist})`}
                </div>
                <div className="text-[10px] text-neutral-600 mt-1">Adjusted for tonight's temp, wind & park altitude</div>
              </div>
            </div>
          </div>
        </div>
        <p className="text-[10px] text-neutral-600 mt-3">Dimensions are official listed distances. Projection is a simplified carry model for research and entertainment.</p>
      </div>
    </div>
  );
}
